import { isEmptyObject } from "../../utilities/objectHelper";
import {
  ChartConfiguration,
  ConditionConfiguration,
  FieldConfiguration,
  FieldConfigurationType,
  PivotConfiguration,
  ReportConfiguration,
  ReportType,
  TabularConfiguration,
  TileConfiguration,
} from "../api/biClient.types";
import { chartRelatedReportTypes } from "./chartRelatedReport.types";
import { isPivotReportType, isTabularReportType, isTileReportType } from "./reportTypeHelper";

export const getDimensionNames = (
  reportType: ReportType | undefined,
  configuration: ReportConfiguration | undefined
) => {
  if (configuration === undefined || reportType === undefined) {
    return [];
  }
  if (isPivotReportType(reportType)) {
    const { conditions, rows, columns } = configuration as PivotConfiguration;
    return getPivotDimensionNames(conditions, rows, columns);
  }
  if (isTabularReportType(reportType)) {
    const { fields, conditions } = configuration as TabularConfiguration;
    return getTabularDimensionNames(fields, conditions);
  }
  if (chartRelatedReportTypes.includes(reportType)) {
    const { conditions, arguments: argumentFields } = configuration as ChartConfiguration;
    return getChartDimensionNames(conditions, argumentFields);
  }
  if (isTileReportType(reportType)) {
    const { fields, conditions } = configuration as TileConfiguration;
    return getTileDimensionNames(fields, conditions);
  }
  throw new Error("Report type not supported");
};

export const getPivotDimensionNames = (
  conditions: ConditionConfiguration[],
  rows: FieldConfiguration[],
  columns: FieldConfiguration[]
) => {
  if (isEmptyObject(conditions) && isEmptyObject(rows) && isEmptyObject(columns)) {
    return [];
  }
  return conditions
    .map((c) => c.filter.dimensionName)
    .concat(rows.map((r) => r.name))
    .concat(columns.map((r) => r.name))
    .sort();
};

export const getTabularDimensionNames = (fields: FieldConfiguration[], conditions: ConditionConfiguration[]) => {
  if (isEmptyObject(fields) && isEmptyObject(conditions)) {
    return [];
  }
  const dimensionFields = fields
    .filter((f) => f.type === FieldConfigurationType.Dimension)
    .map((f) => f.name)
    .filter((f): f is string => !!f)
    .sort();

  return conditions
    .map((c) => c.filter.dimensionName)
    .concat(dimensionFields)
    .sort();
};

export const getChartDimensionNames = (conditions: ConditionConfiguration[], argumentFields: FieldConfiguration[]) => {
  if (isEmptyObject(conditions) && isEmptyObject(argumentFields)) {
    return [];
  }
  return conditions
    .map((c) => c.filter.dimensionName)
    .concat(argumentFields.map((r) => r.name))
    .sort();
};

export const getTileDimensionNames = (fields: FieldConfiguration[], conditions: ConditionConfiguration[]) => {
  return getTabularDimensionNames(fields, conditions);
};
